import React, { useState, useEffect, useRef, FormEvent } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { FaChevronDown, FaChevronUp, FaUserCircle, FaTrashAlt, FaReply, FaTimes, FaImage } from 'react-icons/fa';
import api from "../api/axiosInstance"; // Adjust the import path as necessary
import { RootState } from '../store/store';

interface CommentSectionProps {
    postId: number;
    commentsCount: number;
    autoLoad?: boolean; // Optional prop, defaults to false if not provided
}

interface CommentDto {
    id: number;
    comment: string;
    commentedDate: string;
    fkPost: number;
    fkUser?: number | null;
    fullName: string;
    isAdmin: boolean;
    parentCommentId?: number | null;
    pictureFileId?: number | null;
}

// A comment plus its nested replies, built from the flat list the API returns.
type CommentNodeData = CommentDto & { children: CommentNodeData[] };

// Cap how far replies visually indent so deep threads don't run off-screen.
const MAX_INDENT_LEVEL = 5;
const INDENT_STEP = 20;

const buildCommentTree = (list: CommentDto[]): CommentNodeData[] => {
    const byId: Record<number, CommentNodeData> = {};
    const roots: CommentNodeData[] = [];
    list.forEach((c) => {
        byId[c.id] = { ...c, children: [] };
    });
    list.forEach((c) => {
        const node = byId[c.id];
        if (c.parentCommentId != null && byId[c.parentCommentId]) {
            byId[c.parentCommentId].children.push(node);
        } else {
            roots.push(node);
        }
    });
    return roots;
};

const CommentsContainer = styled.div`
    width: 100%;
    margin-top: 20px;
    padding: 10px;
    border-top: 1px solid #ddd;
`;

const CommentToggle = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    margin-bottom: 10px;
`;

const CommentToggleText = styled.span`
    font-weight: bold;
    color: #343434;
`;

const CommentList = styled.div`
    margin-top: 10px;
`;

const Comment = styled.div`
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    justify-content: space-between;
`;

const Avatar = styled.div`
    margin-right: 10px;
    font-size: 2em;
    color: #3ada5a;
`;

const CommentContent = styled.div`
    display: flex;
    flex-direction: column;
    text-align: left;
    width: 100%;
    max-width: 90%;
    word-wrap: break-word;
`;

const CommentAuthor = styled.span`
    font-weight: bold;
    margin-bottom: 5px;
`;

const CommentText = styled.p`
    margin: 0;
    font-size: 0.9em;
`;

const CommentImageEl = styled.img`
    margin-top: 6px;
    max-width: 220px;
    max-height: 260px;
    width: auto;
    height: auto;
    border-radius: 8px;
    display: block;
`;

const ReplyLink = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    color: #f56607;
    font-weight: bold;
    font-size: 0.8em;
    padding: 2px 0;
    margin-top: 4px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    align-self: flex-start;
`;

const AddCommentForm = styled.form`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
`;

const ReplyingBanner = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fdece0;
    color: #a8480a;
    border-radius: 5px;
    padding: 6px 10px;
    font-size: 0.85em;
`;

const CancelReply = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    color: #a8480a;
    display: inline-flex;
`;

const TextArea = styled.textarea`
    margin: 10px 0;
    padding: 10px;
    width: 100%;
    height: 100px;
    box-sizing: border-box;
    border-radius: 5px;
    border: 1px solid #ddd;
`;

const PreviewWrap = styled.div`
    position: relative;
    align-self: flex-start;
    margin-bottom: 8px;
`;

const PreviewImg = styled.img`
    width: 90px;
    height: 90px;
    object-fit: cover;
    border-radius: 8px;
    display: block;
`;

const RemovePreview = styled.button`
    position: absolute;
    top: -8px;
    right: -8px;
    background: #fff;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: #f56607;
    display: inline-flex;
    padding: 2px;
`;

const FormActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const AttachButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px;
    border: 1px solid #f56607;
    background: transparent;
    color: #f56607;
    border-radius: 5px;
    cursor: pointer;
`;

const SubmitButton = styled.button`
    flex: 1;
    padding: 10px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
`;

const DeleteButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    color: red;

    &:hover {
        color: darkred;
    }
`;

const ImageContainer = styled.div`
  width: 32px;  // Circle dimensions
  height: 32px;
  border-radius: 50%;  // Circle shape
  overflow: hidden;  // Ensures the image is clipped to the circle
`;

const AdminImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;  // Ensures the image scales properly
`;

// Module-level cache so a comment image is fetched once and reused across mounts/rerenders.
// Object URLs are intentionally kept for the page lifetime (bounded by the number of distinct images).
const commentImageCache = new Map<number, string>();

// Loads an image attached to a comment (auth'd fetch → cached object URL).
const CommentImage: React.FC<{ fileId: number }> = ({ fileId }) => {
    const [src, setSrc] = useState<string | null>(() => commentImageCache.get(fileId) ?? null);

    useEffect(() => {
        const cached = commentImageCache.get(fileId);
        if (cached) {
            setSrc(cached);
            return;
        }
        let active = true;
        (async () => {
            try {
                const resp = await api.get(`/posts/file/${fileId}`, { responseType: 'blob' });
                const objectUrl = URL.createObjectURL(resp.data);
                commentImageCache.set(fileId, objectUrl);
                if (active) setSrc(objectUrl);
            } catch (error) {
                console.error(`Error loading comment image ${fileId}:`, error);
            }
        })();
        return () => {
            active = false;
        };
    }, [fileId]);

    if (!src) return null;
    return <CommentImageEl src={src} alt="obrázok" />;
};

interface CommentNodeProps {
    node: CommentNodeData;
    depth: number;
    user: RootState['auth']['user'];
    onReply: (node: CommentNodeData) => void;
    onDelete: (commentId: number) => void;
}

const CommentNode: React.FC<CommentNodeProps> = ({ node, depth, user, onReply, onDelete }) => (
    <div style={{ marginLeft: Math.min(depth, MAX_INDENT_LEVEL) * INDENT_STEP }}>
        <Comment>
            <div style={{ display: 'flex' }}>
                <Avatar>
                    {node.isAdmin ? (
                        <ImageContainer>
                            <AdminImage src={require('../components/g.png')} alt="Admin" />
                        </ImageContainer>
                    ) : (
                        <FaUserCircle size={32} color="#3ada5a" />
                    )}
                </Avatar>
                <CommentContent>
                    <CommentAuthor>{node.fullName}</CommentAuthor>
                    {node.comment?.trim() ? <CommentText>{node.comment}</CommentText> : null}
                    {node.pictureFileId ? <CommentImage fileId={node.pictureFileId} /> : null}
                    {user && (
                        <ReplyLink type="button" onClick={() => onReply(node)}>
                            <FaReply size={12} /> Odpovedať
                        </ReplyLink>
                    )}
                </CommentContent>
            </div>
            {(user && (node.fkUser === user.id || user.isAdmin)) && (
                <DeleteButton onClick={() => onDelete(node.id)}>
                    <FaTrashAlt />
                </DeleteButton>
            )}
        </Comment>
        {node.children.map((child) => (
            <CommentNode
                key={child.id}
                node={child}
                depth={depth + 1}
                user={user}
                onReply={onReply}
                onDelete={onDelete}
            />
        ))}
    </div>
);

const CommentSection: React.FC<CommentSectionProps> = ({ postId, commentsCount, autoLoad = false }) => {
    const [comments, setComments] = useState<CommentDto[]>([]);
    const [isExpanded, setIsExpanded] = useState(autoLoad); // Initialize based on autoLoad
    const [newComment, setNewComment] = useState('');
    const [replyTo, setReplyTo] = useState<{ id: number; name: string } | null>(null);
    const [pendingImageId, setPendingImageId] = useState<number | null>(null);
    const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previewUrlRef = useRef<string | null>(null);

    const user = useSelector((state: RootState) => state.auth.user);
    const fullName = user ? user.fullName : 'Guest';

    useEffect(() => {
        if (isExpanded || autoLoad) {
            loadComments();
        }
    }, [isExpanded, autoLoad]);

    // Revoke the pending-image preview object URL when the section unmounts.
    useEffect(() => () => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    }, []);

    const loadComments = async () => {
        try {
            const response = await api.get<CommentDto[]>(`/comment/all/${postId}`);
            setComments(response.data);
        } catch (error) {
            console.error('Error loading comments:', error);
        }
    };

    const handleReply = (node: CommentNodeData) => {
        setReplyTo({ id: node.id, name: node.fullName });
    };

    const clearPendingImage = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPendingImageId(null);
        setPendingImagePreview(null);
    };

    const onPickImage = () => fileInputRef.current?.click();

    const onImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;

        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const previewUrl = URL.createObjectURL(file);
        previewUrlRef.current = previewUrl;
        setPendingImagePreview(previewUrl);
        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const resp = await api.post('/files/uploadImage', formData);
            setPendingImageId(resp.data as number);
        } catch (error) {
            console.error('Error uploading comment image:', error);
            clearPendingImage();
        } finally {
            setUploadingImage(false);
        }
    };

    const handleAddComment = async (e: FormEvent) => {
        e.preventDefault();
        if (uploadingImage) return;
        if (newComment.trim().length === 0 && pendingImageId == null) return;

        const commentData = {
            id: null,
            comment: newComment,
            commentedDate: new Date().toISOString(),
            fkPost: postId,
            fkUser: user ? user.id : null,
            fullName,
            parentCommentId: replyTo ? replyTo.id : null,
            pictureFileId: pendingImageId ?? null,
        };

        try {
            await api.post('/comment', commentData);
            setNewComment('');
            setReplyTo(null);
            clearPendingImage();
            await loadComments();
        } catch (error) {
            console.error('Error posting comment:', error);
        }
    };

    const handleDeleteComment = async (commentId: number) => {
        try {
            await api.delete(`/comment/${commentId}`);
            // Backend cascade-deletes nested replies, so refetch to stay in sync.
            await loadComments();
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const toggleComments = () => {
        setIsExpanded(!isExpanded);
    };

    const tree = buildCommentTree(comments);

    return (
        <CommentsContainer>
            <CommentToggle onClick={toggleComments}>
                <CommentToggleText>Komentáre ({commentsCount})</CommentToggleText>
                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </CommentToggle>
            {isExpanded && (
                <>
                    <CommentList>
                        {tree.map((node) => (
                            <CommentNode
                                key={node.id}
                                node={node}
                                depth={0}
                                user={user}
                                onReply={handleReply}
                                onDelete={handleDeleteComment}
                            />
                        ))}
                    </CommentList>
                    <AddCommentForm onSubmit={handleAddComment}>
                        {replyTo && (
                            <ReplyingBanner>
                                <span>Odpovedáte používateľovi {replyTo.name}</span>
                                <CancelReply type="button" onClick={() => setReplyTo(null)}>
                                    <FaTimes />
                                </CancelReply>
                            </ReplyingBanner>
                        )}
                        <TextArea
                            placeholder={replyTo ? 'Napíšte odpoveď...' : 'Napíšte komentár...'}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                        />
                        {pendingImagePreview && (
                            <PreviewWrap>
                                <PreviewImg src={pendingImagePreview} alt="náhľad" />
                                <RemovePreview type="button" onClick={clearPendingImage}>
                                    <FaTimes />
                                </RemovePreview>
                            </PreviewWrap>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={onImageSelected}
                        />
                        <FormActions>
                            <AttachButton type="button" onClick={onPickImage} disabled={uploadingImage}>
                                <FaImage /> {uploadingImage ? 'Nahrávam…' : 'Obrázok'}
                            </AttachButton>
                            <SubmitButton
                                className="btn-gradient"
                                type="submit"
                                disabled={uploadingImage || (newComment.trim().length === 0 && pendingImageId == null)}
                            >
                                {replyTo ? 'ODPOVEDAŤ' : 'PRIDAŤ'}
                            </SubmitButton>
                        </FormActions>
                    </AddCommentForm>
                </>
            )}
        </CommentsContainer>
    );
};

export default CommentSection;
